// Funções PURAS do push remoto: decidir para onde um toque leva, limpar o que
// volta para o backend e montar o corpo do registro do aparelho. Sem import de
// Capacitor, store ou router de propósito — é o que deixa o arquivo testável no
// Vitest (tests/unit/push) e é aqui que mora a parte sensível a segurança: o
// `data` de um push chega de fora e vira navegação.
//
// O `data` chega diferente em cada plataforma:
//   - Android: os extras do Intent de toque — só as chaves do FCM (`google.*`,
//     `from`, `collapse_key`) + as nossas, com alguns valores numéricos.
//   - iOS: o `userInfo` INTEIRO — `aps` (objeto), `gcm.message_id`,
//     `google.c.*` e as nossas chaves na raiz.
// Por isso nada aqui itera o objeto recebido para decidir rota: lemos SÓ as
// chaves do contrato (STORY-1642, contrato da fatia 0, seção 6).

export const PUSH_DATA_KEYS = ['v', 'type', 'route', 'enrollId', 'loId', 'courseId', 'appId', 'nid', 'batchId', 'cid', 'subscriptionId', 'variant'];

export const PUSH_TYPES = {
  COMMENT_REPLY: 'comment.reply',
  CONTENT_NEW: 'content.new',
  CERTIFICATE_ISSUED: 'certificate.issued',
  SUBSCRIPTION_LATE: 'subscription.late'
};

// Tipo do sino (els.TiposNotificacoes, `typeId` do GET /notifications) do
// certificado disponível (sc-1616). É o único aviso do sino sem aula.
export const NOTIFICATION_TYPE_CERTIFICATE = 7;

export const HOME_PATH = '/p/my-courses';

// A rota de aula é a ÚNICA aceita vinda do push. Âncoras de início e fim: sem
// elas `/p/course/1/class/2/../../../e/x` ou um prefixo qualquer passariam.
const CLASS_ROUTE_RE = /^\/p\/course\/(\d+)\/class\/(\d+)$/;
const COURSE_ROUTE_RE = /^\/p\/course\/(\d+)$/;
const DIGITS_RE = /^\d+$/;
// `#hh:mm:ss` (ou `#mm:ss`) é o salto de tempo que a StudentClass já entende.
const TIME_HASH_RE = /^#\d{1,2}:\d{2}(:\d{2})?$/;
// Comentários da aula: `#comentarios` abre a aba; `#comentario-{id}` abre e rola até ele.
const COMMENT_HASH_RE = /^#comentario(?:s|-(\d+))$/;
export const COMMENTS_HASH = '#comentarios';
// Curso com `#certificado`: a página do curso abre o modal "Meus Certificados"
// da matrícula (push e sino de certificado disponível, sc-1616).
export const CERTIFICATE_HASH = '#certificado';
// Links de app (Universal Links / appUrlOpen) aceitam um pouco mais que o push:
// também curso, meus cursos e meus downloads. Nada de login, `#tk=`, ebook ou
// qualquer rota que aceite token na URL.
const STATIC_LINK_PATHS = ['/p/my-courses', '/p/my-downloads'];

// Chaves que o FCM/APNs injetam e que não são nossas. Ficam fora do eco para o
// backend: além de ruído, o `aps` é objeto e o contrato do push-events é um
// dicionário string -> string (no máximo 20 chaves, valor <= 200).
const FOREIGN_KEY_RE = /^(google\.|gcm\.|fcm_options)/;
const FOREIGN_KEYS = ['aps', 'from', 'collapse_key', 'notification'];
const PUSH_EVENT_MAX_KEYS = 20;
const PUSH_EVENT_MAX_VALUE = 200;

function asTrimmedString (value) {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function asDigits (value) {
  const str = asTrimmedString(value);
  return str && DIGITS_RE.test(str) ? str : null;
}

// Só as chaves do contrato, só valores primitivos, como string. Qualquer outra
// coisa (objeto, array, função) é descartada sem erro.
export function pickPushData (raw) {
  const picked = {};
  if (!raw || typeof raw !== 'object') return picked;
  for (const key of PUSH_DATA_KEYS) {
    const value = asTrimmedString(raw[key]);
    if (value) picked[key] = value;
  }
  return picked;
}

// Hash da aula para abrir nos comentários: no comentário, se o id for válido; senão só a aba.
export function commentHash (commentId) {
  const id = asDigits(commentId);
  return id ? `#comentario-${id}` : COMMENTS_HASH;
}

// Lê o hash da aula. `null` = não é de comentários (pode ser o salto de tempo);
// `{ commentId: null }` = só abrir a aba.
export function parseCommentHash (hash) {
  if (typeof hash !== 'string') return null;
  const match = COMMENT_HASH_RE.exec(hash);
  return match ? { commentId: match[1] || null } : null;
}

function classTarget (enrollId, loId, extra) {
  return Object.assign({
    name: 'class',
    path: `/p/course/${enrollId}/class/${loId}`,
    params: { enrollId, id: loId },
    enrollId,
    loId
  }, extra);
}

function courseTarget (enrollId, extra) {
  return Object.assign({
    name: 'course',
    path: `/p/course/${enrollId}`,
    params: { id: enrollId },
    enrollId,
    loId: null
  }, extra);
}

// "Minhas assinaturas" (push de assinatura em atraso, sc-1616): não é rota do
// app — quem navega (pushRouter) gera o link do dashboard para o aluno LOGADO e
// abre no navegador do app. Sem `enrollId` de propósito: no cartão o acesso pode
// já ter sido revogado, e a matrícula não estaria mais na lista do aluno.
function subscriptionsTarget (extra) {
  return Object.assign({ name: 'subscriptions', path: null, params: {}, enrollId: null, loId: null }, extra);
}

function homeTarget (extra) {
  return Object.assign({ name: 'my-courses', path: HOME_PATH, params: {}, enrollId: null, loId: null }, extra);
}

// Uma rota interna é aceita só se casar exatamente com a de aula. `//` é recusado
// mesmo antes da regex: é o que transformaria um path em URL de outro host
// ("//evil.com") num router mal configurado ou num fallback de window.location.
function parseClassRoute (route) {
  if (typeof route !== 'string' || route.indexOf('//') !== -1) return null;
  const match = CLASS_ROUTE_RE.exec(route);
  return match ? { enrollId: match[1], loId: match[2] } : null;
}

// Decide o destino do toque numa notificação push.
//
//   - `{ name:'class' }` -> abrir a aula (enrollId + loId)
//   - `{ name:'course', hash:'#certificado' }` -> certificado disponível (enrollId)
//   - `{ name:'subscriptions' }` -> assinatura em atraso ("Minhas assinaturas")
//   - `{ name:'my-courses' }` -> fallback seguro
//
// Push de outra conta (aparelho compartilhado) não abre conteúdo alheio: quem
// navega (pushRouter) só abre matrícula que está na lista do aluno logado.
export function resolvePushTarget (raw) {
  const data = pickPushData(raw);

  const extra = {
    type: data.type || null,
    courseId: asDigits(data.courseId),
    appId: asDigits(data.appId),
    nid: asDigits(data.nid)
  };

  if (data.type === PUSH_TYPES.SUBSCRIPTION_LATE) return subscriptionsTarget(extra);

  const enrollId = asDigits(data.enrollId);
  // Certificado: só o enrollId importa (a route de curso não é aceita do push).
  if (data.type === PUSH_TYPES.CERTIFICATE_ISSUED) {
    return enrollId ? Object.assign(courseTarget(enrollId, extra), { hash: CERTIFICATE_HASH }) : homeTarget(extra);
  }

  const fromRoute = parseClassRoute(data.route);
  const loId = asDigits(data.loId);
  let target = null;
  if (fromRoute) target = classTarget(fromRoute.enrollId, fromRoute.loId, extra);
  else if (enrollId && loId) target = classTarget(enrollId, loId, extra);
  if (!target) return homeTarget(extra);

  // Resposta a comentário abre a aula já na aba de comentários, rolando até a resposta (`cid`).
  if (data.type === PUSH_TYPES.COMMENT_REPLY) target.hash = commentHash(data.cid);
  return target;
}

function normalizePath (pathname) {
  if (typeof pathname !== 'string') return null;
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function targetFromPath (pathname, hash) {
  const path = normalizePath(pathname);
  if (!path || path.indexOf('//') !== -1) return null;

  const cls = CLASS_ROUTE_RE.exec(path);
  if (cls) {
    const target = classTarget(cls[1], cls[2], { type: null, courseId: null, appId: null, nid: null });
    if (typeof hash === 'string' && (TIME_HASH_RE.test(hash) || parseCommentHash(hash))) target.hash = hash;
    return target;
  }

  const course = COURSE_ROUTE_RE.exec(path);
  if (course) {
    const target = courseTarget(course[1], { type: null, courseId: null, appId: null, nid: null });
    if (hash === CERTIFICATE_HASH) target.hash = hash;
    return target;
  }

  if (STATIC_LINK_PATHS.indexOf(path) !== -1) {
    return { name: null, path, params: {}, enrollId: null, loId: null, type: null, courseId: null, appId: null, nid: null };
  }

  return null;
}

function safeParseUrl (url) {
  if (typeof url !== 'string' || !url.trim()) return null;
  try {
    // A base só serve para aceitar path relativo ("/p/..."); o host dela nunca é
    // usado para decidir nada.
    return new URL(url.trim(), 'https://relative.invalid');
  } catch (e) {
    return null;
  }
}

// Link que abriu o app (Universal Link no iOS, App Link/intent no Android, via
// `appUrlOpen` ou `getLaunchUrl`). Exige https e host conhecido: o Android
// entrega à MainActivity qualquer Intent VIEW endereçado explicitamente a ela,
// então o host não é garantia do SO. `null` = ignorar (o app só abre).
export function resolveLinkTarget (url, { allowedHosts = [] } = {}) {
  const parsed = safeParseUrl(url);
  if (!parsed || parsed.protocol !== 'https:') return null;
  // `https:////evil.com` e afins: o parser normaliza o path, então a checagem de
  // `//` precisa olhar a string crua também.
  if (/^https:\/\/\/|^https:\/\/[^/]*\/\//i.test(url.trim())) return null;
  const host = parsed.host.toLowerCase();
  const allowed = allowedHosts.map(h => String(h || '').toLowerCase()).filter(Boolean);
  if (allowed.indexOf(host) === -1) return null;
  return targetFromPath(parsed.pathname, parsed.hash);
}

// Link do sino (`linkRedirect` = `{FrontEnd:Host}p/course/{enrollId}/class/{loId}`).
// O host aqui pode ser o central e o aluno estar no domínio do produtor — o que
// importa é o path ser uma rota NOSSA. Prefere os ids estruturados da
// notificação; o link é a segunda opção. `null` = link externo (quem chama decide
// se sai do app).
//
// As notificações com aula são de comentário: a aula abre na aba de comentários.
// A notificação não guarda QUAL comentário (els.Notificacoes), então só a aba. O
// certificado disponível (tipo 7) não tem aula: abre o curso com o modal.
export function resolveNotificationTarget (notification) {
  if (!notification || typeof notification !== 'object') return null;
  const enrollId = asDigits(notification.enrollId);
  if (Number(notification.typeId) === NOTIFICATION_TYPE_CERTIFICATE) {
    if (!enrollId) return null;
    return Object.assign(courseTarget(enrollId, { type: null, courseId: null, appId: null, nid: asDigits(notification.id) }),
      { hash: CERTIFICATE_HASH });
  }
  const loId = asDigits(notification.learningObjectId);
  let target = null;
  if (enrollId && loId) {
    target = classTarget(enrollId, loId, { type: null, courseId: null, appId: null, nid: asDigits(notification.id) });
  } else {
    const parsed = safeParseUrl(notification.linkRedirect);
    if (!parsed) return null;
    target = targetFromPath(parsed.pathname, parsed.hash);
    if (target) target.nid = asDigits(notification.id);
  }
  if (target && target.name === 'class' && !target.hash) target.hash = COMMENTS_HASH;
  return target;
}

// Corpo do POST /students/push-events: ecoa os campos de rastreio do `data` (os
// nossos primeiro, depois qualquer outro que o backend passe a mandar, como
// `category`/`variant`), sem as chaves do FCM/APNs e dentro dos limites do
// contrato.
export function sanitizePushEventData (raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;

  const ordered = PUSH_DATA_KEYS.filter(k => Object.prototype.hasOwnProperty.call(raw, k))
    .concat(Object.keys(raw).filter(k => PUSH_DATA_KEYS.indexOf(k) === -1).sort());

  for (const key of ordered) {
    if (Object.keys(out).length >= PUSH_EVENT_MAX_KEYS) break;
    if (FOREIGN_KEYS.indexOf(key) !== -1 || FOREIGN_KEY_RE.test(key)) continue;
    let value = raw[key];
    if (typeof value === 'boolean') value = String(value);
    value = asTrimmedString(value);
    if (value == null || value === '') continue;
    out[key] = value.length > PUSH_EVENT_MAX_VALUE ? value.slice(0, PUSH_EVENT_MAX_VALUE) : value;
  }
  return out;
}

// O plugin entrega `{ actionId, notification: { id, data, ... } }` no toque e
// `{ notification: { ... } }` no recebimento. Tolerante a formato inesperado.
export function extractNotificationData (event) {
  const data = event && event.notification && event.notification.data;
  return data && typeof data === 'object' ? data : {};
}

// ---- Registro do aparelho ----

const PERMISSIONS = ['granted', 'denied', 'provisional', 'prompt', 'prompt-with-rationale'];

// Estado de um canal Android a partir do `listChannels`. Importância 0 é
// "bloqueado pelo usuário" (fora do enum dos plugins, que começa em 1). Canal que
// não existe = desconhecido (null), não "ativo".
export function channelStatesFromList (channels, ids) {
  const list = Array.isArray(channels) ? channels : [];
  const find = id => list.find(c => c && c.id === id);
  const state = id => {
    const channel = find(id);
    if (!channel || typeof channel.importance !== 'number') return null;
    return channel.importance > 0;
  };
  return { content: state(ids.content), replies: state(ids.replies) };
}

// PUT /students/devices/{installationId} (contrato, seção 2). `channels` só no
// Android: no iOS o backend grava NULL.
export function buildDevicePayload ({ token, platform, appVersion, timeZone, permission, channels }) {
  const body = {
    identifier: String(token || '').slice(0, 512),
    identifierType: 'fcm_token',
    platform: platform === 'ios' ? 'ios' : 'android',
    appVersion: appVersion ? String(appVersion).slice(0, 20) : null,
    timeZone: timeZone ? String(timeZone).slice(0, 64) : null,
    osPermission: PERMISSIONS.indexOf(permission) !== -1 ? permission : 'prompt'
  };
  if (body.platform === 'android' && channels) {
    const ch = {};
    if (typeof channels.content === 'boolean') ch.content = channels.content;
    if (typeof channels.replies === 'boolean') ch.replies = channels.replies;
    if (Object.keys(ch).length) body.channels = ch;
  }
  return body;
}

// UUID v4 a partir de 16 bytes aleatórios (RFC 4122, seção 4.4). Separado do
// gerador para dar para testar o formato sem depender de crypto.
export function uuidV4FromBytes (bytes) {
  const b = Array.from(bytes || []).slice(0, 16);
  while (b.length < 16) b.push(0);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = b.map(x => (x & 0xff).toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

// ---- Pedido de permissão contextual ----

// Regras do pedido (plano, seção 8 / EAD-Aluno, "Telas"):
//   - só no app, com o push funcionando neste build;
//   - nunca no 1º boot, exceto na 1ª entrada na área logada (sc-6255: quem só
//     assiste nunca comentava nem concluía aula, e ficava sem aviso nenhum);
//   - nunca de novo depois de um "Agora não" nos gatilhos de comentário e aula;
//   - uma vez por gatilho (entrada, comentário, 1ª aula concluída);
//   - permissão já concedida = nada a pedir.
// `denied` NÃO bloqueia: a sheet troca o CTA para "Ativar nos Ajustes".
export function shouldAskPermission ({ trigger, isApp, available, bootCount, promptState, permission }) {
  if (!isApp || available === false) return false;
  if (!trigger) return false;
  if (trigger !== 'entry' && !(bootCount >= 2)) return false;
  const state = promptState || {};
  if (state.declinedAt) return false;
  if (state.shown && state.shown[trigger]) return false;
  if (permission === 'granted' || permission === 'provisional' || permission === 'unsupported') return false;
  return true;
}

// ---- Retentativa do token ----

// Espera antes da N-ésima retentativa do `getToken` (0-based): 5 s, 10 s, 20 s…
// até `maxMs`, e `null` depois de `limit` tentativas — aí só o próximo resume
// tenta de novo. Sem teto, um aparelho que nunca recebe o token APNs (build mal
// assinado, `didFailToRegister`) chamaria o bridge a cada 5 s a sessão inteira.
export function tokenRetryDelay (attempt, { baseMs = 5000, maxMs = 5 * 60 * 1000, limit = 6 } = {}) {
  if (!Number.isInteger(attempt) || attempt < 0 || attempt >= limit) return null;
  return Math.min(baseMs * Math.pow(2, attempt), maxMs);
}
